import React, { Dispatch, SetStateAction } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';

import { Box, Button, Divider, Drawer, Typography } from '@mui/material';

import { RootState } from '../../../../app/rootReducer';
import { AppDispatch } from '../../../../app/store';
import {
  FlexGrid,
  FlexRow,
  NoraButton,
  Spacer,
} from '../../../../common/components/v2';
import { Decision } from '../../../../common/constants/decision';
import { Resource } from '../../../../common/constants/resource';
import ElementLocators from '../../../../common/ElementLocators';
import {
} from '../../decision/decisionSlice';
import { useDecisionSubmit } from '../../decision/useDecisionSubmit';
import { IDecision } from '../../interfaces/IDecision';
import { IDecisionRequest } from '../../interfaces/IDecisionRequest';
import { IDecisionUpdateRequest } from '../../interfaces/IDecisionUpdateRequest';
import { IRiderInfo } from '../../interfaces/IRiderInfo';
import styles from '../styles.module.scss';
import { IneligibleFormData } from './useIneligibleForm';

const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const ReviewIneligible = ({
  applicationId,
  isOpen,
  setIsOpen,
  setIsReview,
  editedDecision,
  riders,
  formData,
  isEditLeadingToCLR,
}: {
  applicationId: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsReview: Dispatch<SetStateAction<boolean>>;
  editedDecision?: IDecision;
  riders?: IRiderInfo[];
  formData: IneligibleFormData;
  isEditLeadingToCLR?: boolean;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { submitCreate, submitEdit } = useDecisionSubmit(applicationId, 'ineligible');
  const eligibleRiders = (riders || [])
    ?.filter(r => r.isEligible && r.isUnderwritable)
    .map(r => r.rider);
  const ineligibleRiders = (riders || [])
    ?.filter(r => !r.isEligible && r.isUnderwritable)
    .map(r => r.rider);
  return (
    <Drawer
      open={isOpen}
      anchor="right"
      variant="persistent"
      classes={{ paper: styles.Drawer }}
      transitionDuration={{ enter: 0, exit: 0 }}
    >
      <Box mt={6}>
        <Box
          m={3}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1">
            {Resource.INELIGIBLE_REVIEW_APPLICATION_HEADER}
          </Typography>
          <Button onClick={() => setIsReview(false)}>
            <Typography variant="subtitle2" color="textSecondary">
              Back
            </Typography>
          </Button>
        </Box>
        <Divider />
        <Box m={3}>
          <FlexGrid>
            {!!editedDecision && (
              <>
                <Spacer height={32} />
                <FlexRow className={styles.DrawerSubTitle}>
                  {Resource.UPDATE_REASON_HEADER}
                </FlexRow>
                <Spacer height={4} />
                <FlexRow>
                  <Typography>{formData.updateReason}</Typography>
                </FlexRow>
                <Spacer height={24} />
              </>
            )}
            {eligibleRiders && eligibleRiders.length > 0 && (
              <>
                <FlexRow className={styles.DrawerSubTitle}>
                  {Resource.ELIGIBLE_RIDERS}
                </FlexRow>
                {eligibleRiders.map(rider => (
                  <FlexRow key={rider}>
                    <span className={styles.Info}>{`${rider}`}</span>
                  </FlexRow>
                ))}
              </>
            )}
            {ineligibleRiders && ineligibleRiders.length > 0 && (
              <>
                <FlexRow className={styles.DrawerSubTitle}>
                  {Resource.INELIGIBLE_RIDERS}
                </FlexRow>
                {ineligibleRiders.map(rider => (
                  <FlexRow key={rider}>
                    <span className={styles.Info}>{`${rider}`}</span>
                  </FlexRow>
                ))}
              </>
            )}
            {formData.mibCodes && formData.mibCodes.length > 0 && (
              <>
                <FlexRow className={styles.DrawerSubTitle}>MIB CODES</FlexRow>
                <Spacer height={4} />
                {formData.mibCodes.map(code => (
                  <FlexRow key={code}>
                    <Typography>{code}</Typography>
                  </FlexRow>
                ))}
                <Spacer height={24} />
              </>
            )}
            <FlexRow className={styles.DrawerSubTitle}>
              INELIGIBLE REASON
            </FlexRow>
            <Spacer height={4} />
            <FlexRow>
              <Typography>{formData.reason}</Typography>
            </FlexRow>
          </FlexGrid>
        </Box>
      </Box>
      <Box>
        <NoraButton
          name={ElementLocators.LOCATOR_SUBMIT_DECLINE_BUTTON}
          className={styles.DrawerButton}
          onClick={() => {
            if (editedDecision) {
              const decisionUpdate = {
                decision: {
                  decision: Decision.INELIGIBLE,
                  decisionType: editedDecision.decisionType,
                  ineligibleReasons: [formData.reason],
                  ineligibleDescription: '-',
                  mibCodes: formData.mibCodes || [],
                  ridersInfo: riders,
                  reviewStatus: editedDecision.reviewStatus,
                } as IDecisionRequest,
                reason: formData.updateReason,
              } as IDecisionUpdateRequest;
              submitEdit(editedDecision.id.toString(), decisionUpdate, isEditLeadingToCLR);
            } else {
              submitCreate({
                reason: formData.reason,
                mibCodes: formData.mibCodes || [],
                riders,
              });
            }
            setIsReview(false);
            setIsOpen(false);
          }}
        >
          {Resource.REVIEW_DECISION}
        </NoraButton>
      </Box>
    </Drawer>
  );
};
